import React, { useRef, useState, useLayoutEffect } from "react";
import { graphql } from "gatsby";
import { Portal } from "react-portal";

import Button from "./Button";
import SaleDate from "./SaleDate";

import routes from "../../clhbid-routes";

import scrollToSection from "../helpers/scroll-to-section";
import useSaleStatuses from "../hooks/use-sale-statuses";
import { isMobileDevice } from "../helpers/media-queries";

const sections = [
  { name: "Overview", value: "overview" },
  { name: "Parcels", value: "parcels" },
  { name: "Description", value: "description" },
  { name: "Questions", value: "questions" },
];

const SaleNav: React.FC<{
  sale: Queries.SaleNavSaleFragment;
}> = ({ sale }) => {
  const activeSections = useRef({} as Record<string, boolean>);
  const [node, setNode] = useState<HTMLElement>();
  const { [sale.slug]: saleStatus } = useSaleStatuses();
  const [activeSection, setActiveSection] = useState("overview");

  useLayoutEffect(() => {
    setNode(document.getElementsByTagName("header")[0]);

    // Add scroll padding to the document element to account for the extra header height caused by the SaleNav
    document.documentElement.classList.toggle("!scroll-pt-[192px]", true);
    document.documentElement.classList.toggle("lg:!scroll-pt-[216px]", true);

    const callback: IntersectionObserverCallback = (entries) => {
      // Update the reference to tell which sections are intersecting
      for (const entry of entries) {
        activeSections.current[entry.target.id] = entry.isIntersecting;
      }

      // Get the first intersecting section
      const newActiveSection = Object.keys(activeSections.current).find(
        (key) => activeSections.current[key] === true
      );

      // If there is a new active section update it, otherwise revert to the last active section
      if (newActiveSection) setActiveSection(newActiveSection);
    };

    const observer = new IntersectionObserver(callback, {
      rootMargin: isMobileDevice() ? "-136px" : "-152px",
      threshold: 0,
    });

    for (const section of sections) {
      const element = document.getElementById(section.value);

      if (element) {
        observer.observe(element);
      }
    }

    return () => {
      document.documentElement.classList.toggle("!scroll-pt-[192px]", false);
      document.documentElement.classList.toggle("lg:!scroll-pt-[216px]", false);

      observer.disconnect();
    };
  }, []);

  if (!node) return null;

  return (
    <Portal node={node}>
      <div className="h-14 overflow-visible lg:h-10">
        <div
          className={[
            "h-14 border-t border-clhbid-charcoal bg-clhbid-gray shadow-2xl transition-[height]",
            activeSection === "overview" ? "lg:h-10" : "lg:h-16",
          ].join(" ")}
        >
          <div className="mx-auto grid h-full max-w-[1200px] grid-cols-[auto,auto] gap-4 px-4 py-2 lg:grid-cols-[1fr,auto,1fr]">
            <div
              className={[
                "hidden grow basis-0 lg:block lg:h-full lg:overflow-hidden lg:transition-[visibility,opacity]",
                activeSection === "overview"
                  ? "lg:invisible lg:opacity-0"
                  : "lg:opacity-1 lg:visible",
              ].join(" ")}
            >
              <h3 className="text-xl font-bold leading-6 text-white">
                {sale.name}
              </h3>
              <SaleDate
                showLive
                type="long"
                sale={sale}
                className="mb-0"
                dateClassName="text-clhbid-silver text-sm italic"
                prefixClassName="text-clhbid-silver text-sm font-bold italic"
                subtextClassName="text-clhbid-silver font-normal text-xs italic"
                liveClassName="text-clhbid-orange font-bold text-base italic"
              />
            </div>
            <div className="block lg:hidden">
              <select
                value={activeSection}
                className="rounded-sm bg-transparent text-white"
                onChange={({ target: { value: newValue } }) => {
                  scrollToSection(newValue);
                }}
              >
                {sections.map((section) => (
                  <option
                    key={section.value}
                    value={section.value}
                    className="text-gray-900"
                  >
                    {section.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="hidden gap-4 lg:flex">
              {sections.map((section) => (
                <button
                  key={section.value}
                  onClick={() => {
                    scrollToSection(section.value);
                  }}
                  className={[
                    "font-semibold capitalize text-white transition-[opacity] hover:text-clhbid-orange",
                    activeSection === "overview" ? "opacity-70" : "opacity-100",
                    section.value === activeSection
                      ? "underline decoration-clhbid-orange underline-offset-8"
                      : "",
                  ].join(" ")}
                >
                  {section.name}
                </button>
              ))}
            </div>
            <div
              className={[
                "flex grow basis-0 items-center justify-end gap-4 lg:h-full lg:overflow-hidden lg:transition-[visibility,opacity]",
                activeSection === "overview"
                  ? "lg:invisible lg:opacity-0"
                  : "lg:opacity-1 lg:visible",
              ].join(" ")}
            >
              <Button
                outline
                href={routes.sale.reminder(sale)}
                state={{ modal: true, noScroll: true }}
                className="hidden py-2 lg:inline-block"
                disabled={saleStatus === "live" || saleStatus === "completed"}
              >
                Set Reminder
              </Button>
              <Button
                external
                href={routes.sale.bidding(sale)}
                className="py-2 lg:min-w-[170px]"
              >
                {saleStatus === "completed" ? "View Results" : "View Bidding"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export const SaleNavFragment = graphql`
  fragment SaleNavSale on ContentfulSale {
    slug
    name
    ...SaleDateSale
  }
`;

export default SaleNav;
